import { useEffect, useRef, useState } from 'react';

function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: 'fa-graduation-cap',
      title: 'Professional Training',
      description: 'Nous proposons des formations pratiques et certifiées.',
      details: [
        'Management & Leadership',
        'IT & Digital Skills',
        'Languages (English, French, etc.)',
        'Soft Skills (Communication, Teamwork)',
      ],
      format: ['In-person', 'Online', 'In-company'],
    },
    {
      icon: 'fa-users',
      title: 'Human Resources Management',
      description: 'Accompagnement stratégique du capital humain.',
      details: [
        'Recrutement',
        'Évaluation des performances',
        'Gestion des carrières',
        'Développement organisationnel',
      ],
      format: ['Sur mesure', 'Audit RH', 'Conseil stratégique'],
    },
    {
      icon: 'fa-handshake-o',
      title: 'Placement and Recruitment',
      description: 'Mettre en relation les meilleurs talents avec les entreprises.',
      details: [
        'Recrutement efficace, rapide et durable',
        'Pour les candidats : comment postuler',
        'Pour les entreprises : demande de profil',
      ],
      format: ['CDI', 'CDD', 'Intérim', 'Freelance'],
    },
  ];

  return (
    <>
      <style>{`
        /* ============================================
           SERVICES – fond noir/bleu profond
           ============================================ */
        .ve-services-page {
          padding: 100px 0 80px;
           max-width: 100%;
          background: #0a1628; /* fond noir-bleu */
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #ffffff;
          position: relative;
          overflow: hidden;
        }

        .ve-services-page::before {
          content: '';
           max-width: 100%;
          position: absolute;
          top: -20%;
          left: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(42, 95, 168, 0.08), transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .container {
          max-width: 100%;
          margin: 0 auto;
          padding: 0 24px;
          position: relative;
          z-index: 2;
        }

        /* En-tête */
        .ve-services-header {
          text-align: center;
          margin-bottom: 60px;
          opacity: 0;
          transform: translateY(30px);
          animation: fadeUp 0.8s ease forwards;
        }

        .ve-section-tag {
          display: inline-block;
          background: rgba(74, 144, 226, 0.15);
          color: #4a90e2;
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          padding: 6px 20px;
          border-radius: 50px;
          text-transform: uppercase;
          margin-bottom: 16px;
        }

        .ve-services-header h2 {
          font-size: clamp(2rem, 4.5vw, 3.2rem);
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 12px;
          letter-spacing: -0.5px;
        }

        .ve-services-header h2 span {
          background: linear-gradient(135deg, #4a90e2, #6bb3e8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .ve-services-header p {
          font-size: 1.1rem;
          color: rgba(255,255,255,0.75);
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.7;
        }

        /* Grille des services */
        .ve-services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
        }

        .ve-service-card {
          background: rgba(255,255,255,0.04);
          backdrop-filter: blur(6px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 24px;
          padding: 32px 28px 28px;
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.4s, background 0.3s;
          opacity: 0;
          transform: translateY(40px);
          animation: cardFadeUp 0.8s ease forwards;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .ve-service-card:nth-child(1) { animation-delay: 0.05s; }
        .ve-service-card:nth-child(2) { animation-delay: 0.10s; }
        .ve-service-card:nth-child(3) { animation-delay: 0.15s; }

        @keyframes cardFadeUp {
          to { opacity: 1; transform: translateY(0); }
        }

        .ve-service-card:hover {
          transform: translateY(-8px) scale(1.01);
          background: rgba(255,255,255,0.08);
          box-shadow: 0 12px 40px rgba(0,0,0,0.4);
          border-color: rgba(74, 144, 226, 0.25);
        }

        .ve-service-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          border-radius: 18px;
          background: rgba(74, 144, 226, 0.15);
          font-size: 2rem;
          color: #4a90e2;
          margin-bottom: 20px;
          transition: transform 0.4s, background 0.3s;
        }

        .ve-service-card:hover .ve-service-icon {
          transform: scale(1.08) rotate(-4deg);
          background: rgba(74, 144, 226, 0.25);
        }

        .ve-service-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 8px;
        }

        .ve-service-card .ve-service-desc {
          font-size: 1rem;
          color: rgba(255,255,255,0.75);
          line-height: 1.6;
          margin: 0 0 18px;
        }

        .ve-service-card .ve-service-details {
          list-style: none;
          padding: 0;
          margin: 0 0 18px;
          flex: 1;
        }

        .ve-service-card .ve-service-details li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 0.95rem;
          color: rgba(255,255,255,0.85);
          padding: 6px 0;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .ve-service-card .ve-service-details li::before {
          content: '▹';
          color: #4a90e2;
          font-size: 1.2rem;
          line-height: 1.2;
        }

        .ve-service-card .ve-service-format {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin: 12px 0 20px;
        }

        .ve-service-card .ve-format-tag {
          background: rgba(74, 144, 226, 0.12);
          color: #4a90e2;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 4px 12px;
          border-radius: 50px;
          border: 1px solid rgba(74, 144, 226, 0.15);
          transition: background 0.3s, color 0.3s;
        }

        .ve-service-card:hover .ve-format-tag {
          background: rgba(74, 144, 226, 0.25);
          color: #ffffff;
        }

        .ve-service-card .ve-btn-service {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: linear-gradient(135deg, #2b7fd4, #4a90e2);
          color: #ffffff;
          font-weight: 600;
          font-size: 0.95rem;
          border-radius: 50px;
          text-decoration: none;
          box-shadow: 0 0 20px rgba(42, 95, 168, 0.25);
          transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
          border: none;
          cursor: pointer;
          margin-top: auto;
          align-self: flex-start;
          position: relative;
          overflow: hidden;
        }

        .ve-service-card .ve-btn-service::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #4a90e2, #2b7fd4);
          opacity: 0;
          transition: opacity 0.4s ease;
          border-radius: 50px;
        }

        .ve-service-card .ve-btn-service:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 0 40px rgba(42, 95, 168, 0.45);
        }

        .ve-service-card .ve-btn-service:hover::before {
          opacity: 1;
        }

        .ve-service-card .ve-btn-service span {
          position: relative;
          z-index: 1;
        }

        /* Animation d'apparition */
        @keyframes fadeUp {
          to { opacity: 1; transform: translateY(0); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .ve-services-page {
            padding: 70px 0 50px;
          }
          .ve-services-grid {
            grid-template-columns: 1fr;
            max-width: 500px;
            margin: 0 auto;
          }
          .ve-service-card {
            padding: 28px 20px 24px;
          }
          .ve-service-card h3 {
            font-size: 1.3rem;
          }
          .ve-service-card .ve-btn-service {
            align-self: center;
          }
        }

        @media (max-width: 480px) {
          .ve-service-card .ve-service-format {
            justify-content: center;
          }
          .ve-service-card .ve-service-details li {
            font-size: 0.9rem;
          }
        }

        /* Fallback FontAwesome */
        .fa, .fas, .far, .fal, .fab {
          font-family: 'Font Awesome 6 Free' !important;
          font-weight: 900;
        }
        .fa-graduation-cap::before { content: "🎓"; font-style: normal; }
        .fa-users::before { content: "👥"; font-style: normal; }
        .fa-handshake-o::before { content: "🤝"; font-style: normal; }
        .fa-arrow-right::before { content: "→"; font-style: normal; }
      `}</style>

      <section className="ve-services-page" ref={sectionRef}>
        <div className="container">
          {/* En-tête */}
          <div className="ve-services-header">
            <span className="ve-section-tag">Nos Services</span>
            <h2>Des solutions <span>sur mesure</span> pour votre organisation</h2>
            <p>
              Découvrez nos trois piliers d'expertise : formation professionnelle,
              gestion des ressources humaines, et placement de talents.
            </p>
          </div>

          {/* Grille des services */}
          <div className="ve-services-grid">
            {services.map((service, index) => (
              <div className="ve-service-card" key={index}>
                <div className="ve-service-icon">
                  <i className={`fa ${service.icon}`}></i>
                </div>
                <h3>{service.title}</h3>
                <p className="ve-service-desc">{service.description}</p>
                <ul className="ve-service-details">
                  {service.details.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
                <div className="ve-service-format">
                  {service.format.map((tag, idx) => (
                    <span className="ve-format-tag" key={idx}>{tag}</span>
                  ))}
                </div>
                <a href="/contact" className="ve-btn-service">
                  <span>En savoir plus</span> <i className="fa fa-arrow-right"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Services;